import { useTranslation } from 'react-i18next';

import { DataPanelAutoWrapInlineItem, DataPanelItem, DataPanelRow, DebugView } from '@/ui';
import type { PanelItemColor } from '@/ui';
import { internetConnectionLabel } from '@/signalr';
import type { Caller } from '@/signalr';

type DeviceInfo = NonNullable<Caller['deviceInfo']>;

export type DeviceStatusRowProps = {
  deviceInfo: DeviceInfo | null;
};

function batteryColor(level: number | null): PanelItemColor {
  if (level == null) {
    return 'none';
  }

  return level > 50 ? 'excellent' : level > 20 ? 'warning' : 'critical';
}

function connectionColor(connectionType: DeviceInfo['internetConnectionType']): PanelItemColor {
  switch (connectionType) {
    case 'NoConnection':
    case 'Cellular2G':
      return 'critical';
    case 'Cellular3G':
      return 'warning';
    case 'Cellular4G':
    case 'Cellular5G':
    case 'WiFi':
      return 'excellent';
    case 'Unknown':
      return 'none';
  }
}

export function DeviceStatusRow({ deviceInfo }: DeviceStatusRowProps) {
  const { t, i18n } = useTranslation();

  if (deviceInfo === null) {
    return <DataPanelAutoWrapInlineItem title={t('Incident_InfoPanel_Device')} showEmptyPlaceholder />;
  }

  const battery =
    deviceInfo.batteryLevel == null
      ? undefined
      : `${new Intl.NumberFormat(i18n.language || 'en', { maximumFractionDigits: 0 }).format(deviceInfo.batteryLevel)} %`;

  return (
    <>
      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_System')}
          value={deviceInfo.system === 'Unknown' ? undefined : deviceInfo.system}
          showEmptyPlaceholder
        />

        <DataPanelItem
          title={t('Incident_InfoPanel_InternetConnectionStrength')}
          value={
            deviceInfo.internetConnectionType === 'Unknown'
              ? undefined
              : internetConnectionLabel(deviceInfo.internetConnectionType)
          }
          color={connectionColor(deviceInfo.internetConnectionType)}
          contentCssClass="text-center"
          showEmptyPlaceholder
        />

        <DataPanelItem
          title={t('Incident_InfoPanel_BatteryLevel')}
          value={battery}
          color={batteryColor(deviceInfo.batteryLevel)}
          contentCssClass="text-end"
          showEmptyPlaceholder
        />
      </DataPanelRow>

      <DebugView>
        <DataPanelAutoWrapInlineItem
          title={t('Incident_InfoPanel_SystemVersion')}
          value={deviceInfo.systemVersion ?? undefined}
          showEmptyPlaceholder
        />

        <DataPanelAutoWrapInlineItem
          title={t('Incident_InfoPanel_LiveVideoSupport')}
          value={t(`Incident_InfoPanel_LiveVideoSupport_${deviceInfo.liveVideoSupport}`)}
        />
      </DebugView>
    </>
  );
}
